import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ButtonOutlined, Flex, Image, Text, SvgIcon } from 'Components/Base';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { useConfig } from 'Context/ConfigContext';

import TargetGoalCard from '../../Components/TargetGoalCard';
import WeeklyStackedBarChart from '../../Components/WeeklyStackedBarChart';
import MyPathProgressTracker from '../MyPathProgressTracker';

const WeeklyProgressWrapper = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  borderRadius: '16px',
  flex: 1,
  minWidth: 0,
  height: '100%',
  width: '100%',
  display: 'flex',
  padding: `${theme.space[3]}px`,
  flexDirection: 'column',
  gap: `${theme.space[2]}px`,
  alignItems: 'flex-start',
}));

const StyledButtonOutlined = styled(ButtonOutlined)(({ theme }) => ({
  backgroundColor: theme.colors.white,
  boxShadow: '0px 12px 24px 0px hsla(0, 0%, 0%, 0.1)',
  padding: `${theme.space[2]}px ${theme.space[3]}px`,
  alignItems: 'center',
  justifyContent: 'space-between',
  display: 'flex',
  width: 'fit-content',
  borderRadius: '8px',
  gap: `${theme.space[2]}px`,
}));

const ResponsiveDisplayFlex = styled(Flex)(({ theme }) => ({
  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    display: 'none',
  },
}));

const ResponsiveWrapper = styled(Flex)(({ theme }) => ({
  [`@media (max-width: ${theme._breakpoints[2]}px)`]: {
    width: '100%',
    alignItems: 'center',
  },
}));

const MyPathWeeklyProgress = ({
  goToMyPath,
  goToMyPathReport,
  weeklyLearningProgresses,
  studentSemesters,
  haveGoals,
  targetHours,
  currentHours,
  mpMyProgressTracker,
  onOpenSeeLastSemesterBlockProductsModal,
  hasPreviousSemester,
}) => {
  const { isLargeTablet } = useCompatibleDisplay();
  const { t } = useTranslation('myPath');
  const { myPath } = useConfig();

  return (
    <WeeklyProgressWrapper>
      <ResponsiveDisplayFlex
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        style={{ width: '100%' }}
      >
        <Flex alignItems={'center'}>
          <Image src="/assets/images/myPath/myPathAnywhereLogo.svg" alt="MyPath Logo" />
          <Text textStyle={'subtitle1'} color={'myPath.primary.500'}>
            MyPath
          </Text>
        </Flex>
        {goToMyPath && (
          <Flex alignItems={'center'} gridColumnGap={2}>
            {myPath.reportUrl && (
              <StyledButtonOutlined onClick={goToMyPathReport} color="secondary">
                <SvgIcon icon={SvgIcon.ICON_LIST.EXCLAMATION_CIRCLE} width={20} height={20} />
                <Text textStyle={'body2'} fontWeight={'600'}>
                  {t('MILESTONE_TRACKER.REPORT_BUTTON')}
                </Text>
              </StyledButtonOutlined>
            )}
            <StyledButtonOutlined onClick={goToMyPath}>
              <Text textStyle={'body2'} fontWeight={'600'}>
                {t('MILESTONE_TRACKER.MYPATH_BUTTON')}
              </Text>
            </StyledButtonOutlined>
          </Flex>
        )}
      </ResponsiveDisplayFlex>
      <Flex flexDirection={'row'} gridColumnGap={4} style={{ width: '100%' }}>
        <ResponsiveWrapper flexDirection={'column'} justifyContent={'space-between'} gridRowGap={1}>
          <ResponsiveWrapper flexDirection={'column'} gridRowGap={1}>
            <Text textStyle={'body1Medium'}>ชั่วโมงเรียนสัปดาห์นี้</Text>
            <WeeklyStackedBarChart
              data={weeklyLearningProgresses}
              studentSemesters={studentSemesters}
              haveGoals={haveGoals}
            />
          </ResponsiveWrapper>
          <TargetGoalCard haveGoals targetHours={targetHours} currentHours={currentHours} />
        </ResponsiveWrapper>
        {isLargeTablet ? null : (
          <MyPathProgressTracker
            mpMyProgressTracker={mpMyProgressTracker}
            onOpenSeeLastSemesterBlockProductsModal={onOpenSeeLastSemesterBlockProductsModal}
            hasPreviousSemester={hasPreviousSemester}
          />
        )}
      </Flex>
    </WeeklyProgressWrapper>
  );
};

MyPathWeeklyProgress.propTypes = {
  goToMyPath: PropTypes.func.isRequired,
  goToMyPathReport: PropTypes.func.isRequired,
  weeklyLearningProgresses: PropTypes.array.isRequired,
  studentSemesters: PropTypes.array.isRequired,
  haveGoals: PropTypes.bool.isRequired,
  targetHours: PropTypes.string.isRequired,
  currentHours: PropTypes.string.isRequired,
  mpMyProgressTracker: PropTypes.object,
  onOpenSeeLastSemesterBlockProductsModal: PropTypes.func,
  hasPreviousSemester: PropTypes.bool,
};

export default MyPathWeeklyProgress;
